/* iter106 — Team Schedule (read-only) for any internal employee.
   Shows the full company schedule for the selected day so the team can coordinate
   ("I'm wrapping up at 2pm — anyone near East Side need a hand?").
   Hits GET /api/employee/team-schedule which returns rows with a my_role flag so
   the calling tech sees a clear "PRIMARY" or "HELPER" badge on jobs they're on. */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Clock, Phone, Users as UsersIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import OnCallBanner from '../../components/OnCallBanner';
import RefreshIndicator from '../../components/RefreshIndicator';
import { AddressLink } from '../../components/MapsNavProvider';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const toISODate = (d) => {
  const x = new Date(d);
  x.setMinutes(x.getMinutes() - x.getTimezoneOffset());
  return x.toISOString().slice(0, 10);
};
const fmtTime12 = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${period}`;
};
const fmtDateLong = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  } catch { return iso; }
};
// Stable color picker so each tech keeps the same swatch across days/jobs.
const TECH_HUES = ['#D4AF37', '#60A5FA', '#34D399', '#F87171', '#A78BFA', '#FB923C', '#F472B6', '#94A3B8'];
const techColor = (id = '') => {
  let h = 0;
  for (let i = 0; i < id.length; i += 1) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return TECH_HUES[h % TECH_HUES.length];
};

const TeamSchedule = () => {
  const [date, setDate] = useState(toISODate(new Date()));
  const [jobs, setJobs] = useState([]);

  const fetchTeam = useCallback(async () => {
    const { data } = await axios.get(`${API}/employee/team-schedule`, {
      withCredentials: true,
      params: { start_date: date, end_date: date },
    });
    setJobs(Array.isArray(data) ? data : []);
  }, [date]);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const { refresh, lastUpdated, isFetching } = useAutoRefresh(fetchTeam, { intervalMs: 30_000 });

  const grouped = jobs.reduce((acc, j) => {
    const key = j.employee_id || 'unassigned';
    if (!acc[key]) acc[key] = { name: j.employee_name || 'Unassigned', jobs: [], id: key };
    acc[key].jobs.push(j);
    return acc;
  }, {});
  const groups = Object.values(grouped).sort((a, b) => {
    if (a.id === 'unassigned') return 1;
    if (b.id === 'unassigned') return -1;
    return (a.name || '').localeCompare(b.name || '');
  });

  const shiftDate = (delta) => {
    const d = new Date(`${date}T00:00:00`);
    d.setDate(d.getDate() + delta);
    setDate(toISODate(d));
  };

  return (
    <div className="space-y-4 p-4" data-testid="team-schedule-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white">Team Schedule</h2>
          <p className="text-[#A3A3A3] text-sm">Company-wide visibility. Read-only.</p>
          <RefreshIndicator lastUpdated={lastUpdated} isFetching={isFetching} refresh={refresh} testId="team-schedule-refresh" />
        </div>
        <div className="flex items-center gap-2 bg-[#0F0F0F] border border-[#262626] rounded-md p-1">
          <button
            type="button"
            data-testid="team-schedule-prev"
            onClick={() => shiftDate(-1)}
            className="px-2 py-1 text-white hover:bg-[#262626] rounded"
            aria-label="Previous day"
          ><ChevronLeft className="w-4 h-4" /></button>
          <span data-testid="team-schedule-date" className="text-white font-medium px-2 min-w-[180px] text-center">
            {fmtDateLong(date)}
          </span>
          <button
            type="button"
            data-testid="team-schedule-next"
            onClick={() => shiftDate(1)}
            className="px-2 py-1 text-white hover:bg-[#262626] rounded"
            aria-label="Next day"
          ><ChevronRight className="w-4 h-4" /></button>
          <button
            type="button"
            data-testid="team-schedule-today"
            onClick={() => setDate(toISODate(new Date()))}
            className="px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded ml-1"
          >Today</button>
        </div>
      </div>

      <OnCallBanner />

      {jobs.length === 0 ? (
        <div className="bg-[#0F0F0F] border border-[#262626] rounded-md p-8 text-center" data-testid="team-schedule-empty">
          <UsersIcon className="w-8 h-8 text-[#525252] mx-auto mb-2" />
          <p className="text-[#A3A3A3] text-sm">No jobs scheduled for the team on {fmtDateLong(date)}.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map((g) => (
            <div
              key={g.id}
              data-testid={`team-schedule-tech-${g.id}`}
              className="bg-[#0F0F0F] border border-[#262626] rounded-md overflow-hidden"
            >
              <div
                className="px-3 py-2 border-b border-[#262626] flex items-center gap-2"
                style={{ borderLeft: `4px solid ${techColor(g.id)}` }}
              >
                <span className="text-white font-bold text-sm truncate flex-1">{g.name}</span>
                <span className="text-[10px] text-[#A3A3A3]">{g.jobs.length} job{g.jobs.length === 1 ? '' : 's'}</span>
              </div>
              <ul className="divide-y divide-[#1F1F1F]">
                {g.jobs.sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '')).map((job) => (
                  <li
                    key={job.id}
                    data-testid={`team-job-${job.id}`}
                    className="p-3 hover:bg-[#141414]"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[#D4AF37] text-xs font-bold flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {job.all_day ? 'All day' : `${fmtTime12(job.start_time)} – ${fmtTime12(job.end_time)}`}
                      </span>
                      {job.my_role === 'primary' && (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#D4AF37] text-[#0A0A0A]">
                          You · Primary
                        </span>
                      )}
                      {job.my_role && job.my_role !== 'primary' && (
                        <span data-testid={`my-role-badge-${job.id}`} className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-700/40">
                          You · {(job.my_role || 'helper').toUpperCase()}
                        </span>
                      )}
                    </div>
                    <p className="text-white text-sm font-medium mt-1 line-clamp-2">
                      {job.shift_title || job.service_type || 'Job'}
                    </p>
                    {job.customer_name && (
                      <p className="text-[#A3A3A3] text-xs mt-0.5">{job.customer_name}</p>
                    )}
                    {(job.pm_company_id || job.pm_company_name) && (
                      <p data-testid={`team-card-requester-${job.id}`} className="text-cyan-300 text-xs mt-0.5">
                        Requested by: {job.requester_state === 'unknown' ? 'Unknown' : (job.pm_requester_name || '—')}
                      </p>
                    )}
                    {job.address && (
                      <AddressLink
                        address={job.address}
                        testId={`team-card-address-${job.id}`}
                        className="text-[#A3A3A3] text-xs mt-0.5 items-start"
                        iconClassName="w-3 h-3 mt-0.5 flex-shrink-0 text-[#D4AF37]"
                      >
                        <span className="line-clamp-1">{job.address}</span>
                      </AddressLink>
                    )}
                    {job.customer_phone && (
                      <a
                        href={`tel:${(job.customer_phone || '').replace(/[^+\d]/g, '')}`}
                        className="text-[#D4AF37] hover:text-[#F3E5AB] text-xs flex items-center gap-1 mt-0.5"
                        data-testid={`team-job-call-${job.id}`}
                      >
                        <Phone className="w-3 h-3" /> {job.customer_phone}
                      </a>
                    )}
                    {(job.helpers || []).length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1" data-testid={`team-job-helpers-${job.id}`}>
                        {(job.helpers || []).map((h) => (
                          <span
                            key={h.employee_id}
                            className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-300 border border-blue-700/30"
                            title={`${h.name} — ${h.role}`}
                          >
                            +{h.name} · {h.role}
                          </span>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeamSchedule;
